import { emit } from '../../events';
import { noteUserGesture } from '../activeAdapter';
import { correctPoint, resolveTouch, type Point } from '../hitTest';
import type { InputAdapter, InputCapabilities, Intent } from '../InputAdapter';

/**
 * Direct touch: the mode this person uses if their targeting is usable at all.
 *
 * Activation happens on **release within the target**, not on press. That is the single most
 * important choice in this file: a finger that lands on the wrong key can be dragged to the right
 * one and then lifted, which turns a mis-hit into a correction rather than a deletion.
 *
 * Targets opt in with `data-activate-id`. The adapter never knows what a target means — it emits
 * intents and the surface decides (§5.1), which is what lets Phase 3 add dwell and scanning with
 * no UI changes.
 */

const CAPABILITIES: InputCapabilities = {
  needsFocusRing: false,
  // A real tap, so `WebSpeech` remains usable and Web Audio needs no special handling.
  isTimed: false,
  needsAudioCue: false,
};

export interface DirectTouchSettings {
  slopPx: number;
  repeatSuppressMs: number;
  /** Keys get their own, much shorter window — see below. */
  keyRepeatSuppressMs: number;
  touchOffsetCorrection: Point;
}

/**
 * Tremor debounce is **per target class**, because the cost of getting it wrong is asymmetric.
 *
 * On `Löschen` or a phrase tile, a doubled activation is expensive (lost text, a sentence said
 * twice) and a suppressed one is cheap — so the window is generous. On a key it inverts: an extra
 * letter is visible and costs one tap, while a *suppressed* letter makes every German word with a
 * double letter untypeable, silently. At 400 ms, "HALLO" comes out "HALO" and nothing explains why.
 *
 * The design doc specifies one 300–600 ms window for everything (§5.1). That is right for actions
 * and wrong for keys, so keys read `keyRepeatSuppressMs` instead.
 */
function suppressWindowFor(targetId: string, settings: DirectTouchSettings): number {
  return targetId.startsWith('key:') ? settings.keyRepeatSuppressMs : settings.repeatSuppressMs;
}

export function createDirectTouchAdapter(getSettings: () => DirectTouchSettings): InputAdapter {
  let root: HTMLElement | null = null;
  let emitIntent: ((intent: Intent) => void) | null = null;

  let pressTargetId: string | null = null;
  let pressTargetEl: HTMLElement | null = null;
  let pressAtMs = 0;
  let lastTargetId: string | null = null;
  let lastAtMs: number | null = null;

  const targetFrom = (event: PointerEvent): HTMLElement | null => {
    const el = event.target;
    if (!(el instanceof Element)) return null;
    return el.closest<HTMLElement>('[data-activate-id]');
  };

  const onPointerDown = (event: PointerEvent): void => {
    // A genuine user gesture. Recorded here rather than on release so the gesture window covers
    // the whole press, which for slow deliberate movement can be several hundred milliseconds.
    noteUserGesture();

    const target = targetFrom(event);
    pressTargetEl = target;
    pressTargetId = target?.dataset.activateId ?? null;
    pressAtMs = performance.now();

    if (target) target.dataset.pressed = 'true';
  };

  const onPointerUp = (event: PointerEvent): void => {
    const settings = getSettings();
    const releaseTarget = targetFrom(event);
    const rect = pressTargetEl?.getBoundingClientRect() ?? null;

    if (pressTargetEl) delete pressTargetEl.dataset.pressed;

    const releasePoint = correctPoint(
      { x: event.clientX, y: event.clientY },
      settings.touchOffsetCorrection,
    );

    const atMs = performance.now();
    const outcome = resolveTouch({
      releasePoint,
      pressTargetId,
      releaseTargetId: releaseTarget?.dataset.activateId ?? null,
      targetRect: rect
        ? { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom }
        : null,
      slopPx: settings.slopPx,
      atMs,
      lastTargetId,
      lastAtMs,
      repeatSuppressMs: suppressWindowFor(pressTargetId ?? '', settings),
    });

    pressTargetId = null;
    pressTargetEl = null;

    switch (outcome.kind) {
      case 'activate':
        lastTargetId = outcome.targetId;
        lastAtMs = atMs;
        emit({
          type: 'KeyActivated',
          key: outcome.targetId,
          // Press to release: what the person experienced, not what our handler cost.
          latencyMs: Math.round(atMs - pressAtMs),
          position: releasePoint,
        });
        emitIntent?.({ kind: 'activate', targetId: outcome.targetId });
        break;

      case 'missed':
        // Nothing happens on screen, but it is recorded: a systematic direction of misses is
        // evidence of a field loss, and only the misses carry it (§5.5).
        emit({ type: 'ActivationMissed', position: releasePoint, offset: outcome.offset });
        break;

      case 'suppressed':
        // Tremor double-tap. Silent on purpose — the person intended one press and got one.
        break;

      case 'ignored':
        break;
    }
  };

  const onPointerCancel = (): void => {
    if (pressTargetEl) delete pressTargetEl.dataset.pressed;
    pressTargetId = null;
    pressTargetEl = null;
  };

  return {
    id: 'directTouch',
    capabilities: CAPABILITIES,

    attach(element: HTMLElement, emitFn: (intent: Intent) => void): void {
      root = element;
      emitIntent = emitFn;
      root.addEventListener('pointerdown', onPointerDown);
      root.addEventListener('pointerup', onPointerUp);
      root.addEventListener('pointercancel', onPointerCancel);
    },

    detach(): void {
      root?.removeEventListener('pointerdown', onPointerDown);
      root?.removeEventListener('pointerup', onPointerUp);
      root?.removeEventListener('pointercancel', onPointerCancel);
      root = null;
      emitIntent = null;
    },
  };
}
